import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BuilderState, Component, LandingPage } from '../types';
import { useAuthStore } from './authStore';
import { normalizeLandingPageComponents } from '../utils/migrateLegacyComponents';

// Production API URL (hardcoded for reliability)
const API_URL = 'https://api.hitzlink.com/api';

interface ExtendedBuilderState extends BuilderState {
  pages: LandingPage[];
  // Loading flags for the dashboard. `isLoadingPages` is true while the
  // metadata fetch is in flight; `hasLoadedPages` flips to true after the
  // first fetch (success OR error) so the dashboard can tell "still
  // loading first time" apart from "loaded, user genuinely has 0 pages".
  // `isLoadingStats` covers the slower per-page analytics fan-out that the
  // dashboard kicks off in the background after rendering the page list.
  isLoadingPages: boolean;
  hasLoadedPages: boolean;
  isLoadingStats: boolean;
  loadPages: () => Promise<void>;
  loadPagesFast: () => Promise<void>;
  loadPageStats: () => Promise<void>;
  createPage: (name: string) => Promise<LandingPage | null>;
  deletePage: (id: string) => Promise<void>;
  clearStore: () => void;
}

export const useBuilderStore = create<ExtendedBuilderState>()(
  persist(
    (set, get) => ({
      currentPage: null,
      selectedComponent: null,
      isPreviewMode: false,
      isSaving: false,
      pages: [],
      isLoadingPages: false,
      hasLoadedPages: false,
      isLoadingStats: false,

      // Load pages metadata + stats in a single (slow) request. Kept for
      // existing callers (createPage / handleDuplicate / handleSlugUpdated /
      // handleSaveRename / handleTogglePublish) that need the freshest
      // numbers right away. The dashboard prefers loadPagesFast +
      // loadPageStats so it can render the page list instantly.
      loadPages: async () => {
        const token = useAuthStore.getState().token;
        if (!token) {
          console.log('No token, skipping page load');
          return;
        }

        set({ isLoadingPages: true });
        try {
          const response = await fetch(`${API_URL}/pages`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error('Failed to load pages');
          }

          const pages = await response.json();

          // Transform database pages to LandingPage format
          const transformedPages: LandingPage[] = (pages || []).map((page: any) => {
            const bgColor = page.settings?.backgroundColor || '#ffffff';
            const bgImage = page.settings?.backgroundImage;
            const bgVideo = page.settings?.backgroundVideo; // ✅ เพิ่ม backgroundVideo!
            
            // ✅ Parse components if it's a JSON string
            let parsedComponents = page.components || [];
            if (typeof parsedComponents === 'string') {
              try {
                parsedComponents = JSON.parse(parsedComponents);
              } catch (e) {
                console.error('Failed to parse components:', e);
                parsedComponents = [];
              }
            }
            
            return {
              id: page.id,
              name: page.name,
              slug: page.slug, // ✅ เพิ่ม slug!
              is_published: page.is_published !== false, // ✅ เพิ่ม is_published (default: true)
              auto_paused_at: page.auto_paused_at || null,
              auto_paused_reason: page.auto_paused_reason || null,
              views: page.views || 0, // ✅ เพิ่ม views!
              uniqueVisitors: page.uniqueVisitors || 0, // ✅ เพิ่ม uniqueVisitors!
              uniqueClicks: page.uniqueClicks || 0, // ✅ เพิ่ม uniqueClicks!
              conversions: page.conversions || 0, // ✅ เพิ่ม conversions!
              components: normalizeLandingPageComponents(parsedComponents),
              settings: {
                backgroundColor: bgColor,
                backgroundImage: bgImage,
                backgroundVideo: bgVideo, // ✅ เพิ่ม backgroundVideo!
                meta: page.settings?.meta || { title: page.name, description: '' },
                analytics: page.settings?.analytics || { facebookPixelIds: [] },
                cloaking: page.settings?.cloaking || { enabled: false, safePageUrl: 'https://www.google.com' },
                redirect: page.settings?.redirect || { enabled: false, desktopUrl: '', mobileUrl: '', tabletUrl: '' },
                seo: page.settings?.seo || { indexing: true },
                branding: page.settings?.branding || { show: true },
                // Preserve any settings the Builder UI doesn't manage directly
                // (e.g. `affiliate` — owned by AffiliatePostbackPanel via PUT
                // /capi/affiliate-config). Without this carry-over, the next
                // builder Save would PUT /pages with a `settings` object that
                // drops these keys → server replaces the whole JSONB column
                // → postbackSecret + affiliate flags get wiped out.
                ...(page.settings?.affiliate ? { affiliate: page.settings.affiliate } : {}),
                ...(page.settings?.lang !== undefined ? { lang: page.settings.lang } : {}),
                ...(page.settings?.layoutWidth !== undefined ? { layoutWidth: page.settings.layoutWidth } : {}),
                ...(page.settings?.maxPageWidth !== undefined ? { maxPageWidth: page.settings.maxPageWidth } : {}),
                ...(page.settings?.outerBackgroundColor !== undefined ? { outerBackgroundColor: page.settings.outerBackgroundColor } : {}),
              },
              createdAt: page.created_at,
              updatedAt: page.updated_at,
            } as any;
          });

          set({ pages: transformedPages, hasLoadedPages: true });
          console.log(`✅ Loaded ${transformedPages.length} pages`);
        } catch (error) {
          console.error('Error loading pages:', error);
          // Don't blow away an already-loaded list on a refresh failure —
          // that would flash the empty state. Only reset if we never had
          // pages to begin with.
          set((state) => ({
            pages: state.pages.length === 0 ? [] : state.pages,
            hasLoadedPages: true,
          }));
        } finally {
          set({ isLoadingPages: false });
        }
      },

      // Fast-path metadata-only loader. Calls /pages?fast=1 which skips the
      // expensive per-page analytics fan-out (paginated counts, unique-IP
      // sets) on the server. Returns in <500ms even for users with dozens
      // of pages and hundreds of thousands of analytics rows. The stats
      // columns initially show whatever counters were stored on the page
      // row itself; loadPageStats() backfills the accurate aggregates.
      loadPagesFast: async () => {
        const token = useAuthStore.getState().token;
        if (!token) return;

        set({ isLoadingPages: true, pages: [], hasLoadedPages: false });
        try {
          const response = await fetch(`${API_URL}/pages?fast=1`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!response.ok) throw new Error('Failed to load pages');
          const pages = await response.json();

          const transformedPages: LandingPage[] = (pages || []).map((page: any) => {
            const bgColor = page.settings?.backgroundColor || '#ffffff';
            const bgImage = page.settings?.backgroundImage;
            const bgVideo = page.settings?.backgroundVideo;
            let parsedComponents = page.components || [];
            if (typeof parsedComponents === 'string') {
              try {
                parsedComponents = JSON.parse(parsedComponents);
              } catch {
                parsedComponents = [];
              }
            }
            return {
              id: page.id,
              name: page.name,
              slug: page.slug,
              is_published: page.is_published !== false,
              auto_paused_at: page.auto_paused_at || null,
              auto_paused_reason: page.auto_paused_reason || null,
              views: page.views || 0,
              uniqueVisitors: page.uniqueVisitors || 0,
              uniqueClicks: page.uniqueClicks || 0,
              conversions: page.conversions || 0,
              statsLoaded: false,
              components: normalizeLandingPageComponents(parsedComponents),
              settings: {
                backgroundColor: bgColor,
                backgroundImage: bgImage,
                backgroundVideo: bgVideo,
                meta: page.settings?.meta || { title: page.name, description: '' },
                analytics: page.settings?.analytics || { facebookPixelIds: [] },
                cloaking: page.settings?.cloaking || { enabled: false, safePageUrl: 'https://www.google.com' },
                redirect: page.settings?.redirect || { enabled: false, desktopUrl: '', mobileUrl: '', tabletUrl: '' },
                seo: page.settings?.seo || { indexing: true },
                branding: page.settings?.branding || { show: true },
                ...(page.settings?.affiliate ? { affiliate: page.settings.affiliate } : {}),
                ...(page.settings?.lang !== undefined ? { lang: page.settings.lang } : {}),
                ...(page.settings?.layoutWidth !== undefined ? { layoutWidth: page.settings.layoutWidth } : {}),
                ...(page.settings?.maxPageWidth !== undefined ? { maxPageWidth: page.settings.maxPageWidth } : {}),
                ...(page.settings?.outerBackgroundColor !== undefined ? { outerBackgroundColor: page.settings.outerBackgroundColor } : {}),
              },
              createdAt: page.created_at,
              updatedAt: page.updated_at,
            } as any;
          });

          set({ pages: transformedPages, hasLoadedPages: true });
        } catch (error) {
          console.error('Error loading pages (fast):', error);
          // Never keep another identity's rows after a failed reload.
          set({ pages: [], hasLoadedPages: true });
        } finally {
          set({ isLoadingPages: false });
        }
      },

      // Background loader for accurate per-page analytics aggregates.
      // Merges into existing page rows without touching anything else, so
      // the dashboard list stays mounted and just sees the stat columns
      // pop in when they arrive.
      loadPageStats: async () => {
        const token = useAuthStore.getState().token;
        if (!token) return;
        const ids = get().pages.map((p) => p.id);
        if (ids.length === 0) return;

        set({ isLoadingStats: true });
        try {
          const response = await fetch(`${API_URL}/pages/stats/by-ids`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ pageIds: ids }),
          });
          if (!response.ok) throw new Error('Failed to load page stats');
          const { stats } = (await response.json()) as {
            stats: Record<
              string,
              { views: number; uniqueVisitors: number; uniqueClicks: number; conversions: number }
            >;
          };

          set((state) => ({
            pages: state.pages.map((p) => {
              const s = stats[p.id];
              if (!s) return p;
              return {
                ...p,
                views: s.views,
                uniqueVisitors: s.uniqueVisitors,
                uniqueClicks: s.uniqueClicks,
                conversions: s.conversions,
                statsLoaded: true,
              } as any;
            }),
          }));
        } catch (error) {
          console.error('Error loading page stats:', error);
        } finally {
          set({ isLoadingStats: false });
        }
      },

      // Create new page via API
      createPage: async (name: string) => {
        const token = useAuthStore.getState().token;
        if (!token) {
          console.error('No token');
          return null;
        }

        try {
          const response = await fetch(`${API_URL}/pages`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              name,
              components: [], // Start with empty page
              published: true, // Auto-publish new pages
              settings: {
                backgroundColor: '#ffffff',
                meta: {
                  title: name,
                  description: '',
                },
                analytics: {
                  facebookPixelIds: [],
                },
                cloaking: {
                  enabled: false,
                  safePageUrl: 'https://www.google.com',
                },
                redirect: {
                  enabled: false,
                  desktopUrl: '',
                  mobileUrl: '',
                  tabletUrl: '',
                },
              },
            }),
          });

          if (!response.ok) {
            const errBody = await response.json().catch(() => ({}));
            const msg =
              (typeof errBody.details === 'string' && errBody.details.trim()) ||
              (typeof errBody.message === 'string' && errBody.message.trim()) ||
              (typeof errBody.error === 'string' && errBody.error.trim()) ||
              'Failed to create page';
            throw new Error(msg);
          }

          const page = await response.json();

          const newPage: LandingPage = {
            id: page.id,
            name: page.name,
            slug: page.slug, // ✅ เพิ่ม slug!
            components: page.components || [],
            settings: page.settings,
            createdAt: page.created_at,
            updatedAt: page.updated_at,
          } as any;

          set((state) => ({
            pages: [newPage, ...state.pages],
            currentPage: newPage,
          }));

          console.log('Page created successfully:', newPage);
          return newPage;
        } catch (error) {
          console.error('Error creating page:', error);
          throw error;
        }
      },

      // Delete page via API
      deletePage: async (id: string) => {
        const token = useAuthStore.getState().token;
        if (!token) {
          console.error('No token');
          return;
        }

        try {
          const response = await fetch(`${API_URL}/pages/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error('Failed to delete page');
          }

          set((state) => ({
            pages: state.pages.filter((p) => p.id !== id),
            currentPage: state.currentPage?.id === id ? null : state.currentPage,
          }));

          console.log('Page deleted successfully');
        } catch (error) {
          console.error('Error deleting page:', error);
          throw error;
        }
      },

      setCurrentPage: (page: LandingPage | null) => {
        if (!page) {
          set({ currentPage: null });
          return;
        }
        set({
          currentPage: {
            ...page,
            components: normalizeLandingPageComponents((page as any).components),
          } as LandingPage,
        });
      },

      updatePage: (updates: Partial<LandingPage>) => {
        set((state) => {
          if (!state.currentPage) return state;
          
          const updatedPage = { ...state.currentPage, ...updates };
          return {
            currentPage: updatedPage,
            pages: state.pages.map((p) => (p.id === updatedPage.id ? updatedPage : p)),
          };
        });
      },

      updatePageSettings: (updates: any) => {
        set((state) => {
          if (!state.currentPage) return state;
          
          const updatedPage = {
            ...state.currentPage,
            settings: {
              ...state.currentPage.settings,
              ...updates,
            },
          };
          
          return {
            currentPage: updatedPage,
            pages: state.pages.map((p) => (p.id === updatedPage.id ? updatedPage : p)),
          };
        });
      },

      addComponent: (component: Component) => {
        set((state) => {
          if (!state.currentPage) return state;
          
          const updatedPage = {
            ...state.currentPage,
            components: [...state.currentPage.components, component],
          };
          
          return {
            currentPage: updatedPage,
            pages: state.pages.map((p) => (p.id === updatedPage.id ? updatedPage : p)),
          };
        });
      },

      updateComponent: (id: string, updates: Partial<Component>) => {
        set((state) => {
          if (!state.currentPage) return state;
          
          let updatedSelectedComponent = state.selectedComponent;
          
          const updatedPage = {
            ...state.currentPage,
            components: state.currentPage.components.map((c) => {
              if (c.id !== id) return c;
              
              // Deep merge settings if provided. Cast through Component to
              // preserve the discriminated union after spread (TS otherwise
              // widens c.settings | updates.settings to the union of all
              // settings shapes which loses the type↔settings link).
              const merged = { ...c, ...updates } as Component;
              if (updates.settings && c.settings) {
                (merged as { settings: unknown }).settings = {
                  ...c.settings,
                  ...updates.settings,
                };
              }
              const updatedComponent = merged;
              
              // Also update selectedComponent if this is the selected one
              if (state.selectedComponent?.id === id) {
                updatedSelectedComponent = updatedComponent;
              }
              
              return updatedComponent;
            }),
          };
          
          return {
            currentPage: updatedPage,
            selectedComponent: updatedSelectedComponent,
            pages: state.pages.map((p) => (p.id === updatedPage.id ? updatedPage : p)),
          };
        });
      },

      deleteComponent: (id: string) => {
        set((state) => {
          if (!state.currentPage) return state;
          
          const updatedPage = {
            ...state.currentPage,
            components: state.currentPage.components.filter((c) => c.id !== id),
          };
          
          return {
            currentPage: updatedPage,
            selectedComponent: state.selectedComponent?.id === id ? null : state.selectedComponent,
            pages: state.pages.map((p) => (p.id === updatedPage.id ? updatedPage : p)),
          };
        });
      },

      reorderComponents: (startIndex: number, endIndex: number) => {
        set((state) => {
          if (!state.currentPage) return state;
          
          const components = Array.from(state.currentPage.components);
          const [removed] = components.splice(startIndex, 1);
          components.splice(endIndex, 0, removed);
          
          const updatedPage = {
            ...state.currentPage,
            components,
          };
          
          return {
            currentPage: updatedPage,
            pages: state.pages.map((p) => (p.id === updatedPage.id ? updatedPage : p)),
          };
        });
      },

      setSelectedComponent: (component: Component | null) => {
        set({ selectedComponent: component });
      },

      // Alias for backward compatibility
      selectComponent: (component: Component | null) => {
        set({ selectedComponent: component });
      },

      setPreviewMode: (isPreview: boolean) => {
        set({ isPreviewMode: isPreview });
      },

      togglePreviewMode: () => {
        set((state) => ({ isPreviewMode: !state.isPreviewMode }));
      },

      savePage: async () => {
        const state = get();
        if (!state.currentPage) return;

        set({ isSaving: true });
        
        const token = useAuthStore.getState().token;
        if (!token) {
          set({ isSaving: false });
          return;
        }

        try {
          const response = await fetch(`${API_URL}/pages/${state.currentPage.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              name: state.currentPage.name,
              slug: (state.currentPage as any).slug, // ✅ ส่ง slug ไปด้วย!
              components: normalizeLandingPageComponents(state.currentPage.components as any),
              settings: state.currentPage.settings,
            }),
          });

          if (!response.ok) {
            // Try to get the actual error message from the response
            let errorMessage = 'Failed to save page';
            try {
              const errorData = await response.json();
              errorMessage = errorData.message || errorData.error || errorMessage;
            } catch (e) {
              // Couldn't parse JSON, use default message
            }
            throw new Error(errorMessage);
          }

          console.log('Page saved successfully');
        } catch (error) {
          console.error('Error saving page:', error);
          throw error;
        } finally {
          set({ isSaving: false });
        }
      },

      // Clear all store data (call on logout)
      clearStore: () => {
        set({
          currentPage: null,
          pages: [],
          selectedComponent: null,
          isPreviewMode: false,
          isLoadingPages: false,
          hasLoadedPages: false,
          isLoadingStats: false,
        });
      },
    }),
    {
      name: 'builder-storage',
      partialize: (state) => ({
        currentPage: state.currentPage,
      }),
    }
  )
);
